/* Mega menu image preview: hovering or focusing a collection link shows its lead product
   on the right. Two stacked images crossfade; images load only when asked for. */
(() => {
  const SWAP_DELAY = 70; // ms of hover before swapping, so sweeping across links stays calm

  const init = (menu) => {
    const panel = menu.querySelector('[data-dvk-mega-preview]');
    if (!panel || panel.dataset.ready) return;
    panel.dataset.ready = 'true';

    const stage = panel.querySelector('.dvk-mega__stage');
    const imgs = [...panel.querySelectorAll('.dvk-mega__img')];
    const title = panel.querySelector('[data-dvk-preview-slot="title"]');
    const count = panel.querySelector('[data-dvk-preview-slot="count"]');
    const start = {
      src: imgs[0] && imgs[0].getAttribute('src'),
      href: stage.getAttribute('href'),
      title: title.textContent,
      count: count.textContent,
    };
    let currentSrc = start.src;
    let currentHref = start.href;
    let token = 0;
    let timer;

    const show = (data) => {
      if (!data.href || data.href === currentHref) return;
      currentHref = data.href;
      const mine = ++token;

      // Caption and link update at once; the picture follows as soon as it has loaded.
      stage.setAttribute('href', data.href);
      title.textContent = data.title;
      count.textContent = data.count;
      panel.classList.remove('is-swapping');
      void panel.offsetWidth; // restart the small entrance motion
      panel.classList.add('is-swapping');

      if (!data.src || data.src === currentSrc) return;
      currentSrc = data.src;
      const front = imgs.find((img) => img.classList.contains('is-current')) || imgs[0];
      const back = imgs.find((img) => img !== front);
      const reveal = () => {
        if (mine !== token) return;
        back.hidden = false;
        back.classList.add('is-current');
        front.classList.remove('is-current');
        panel.classList.remove('is-swapping');
        void panel.offsetWidth;
        panel.classList.add('is-swapping');
      };
      back.onload = reveal;
      back.src = data.src;
      if (back.complete && back.naturalWidth) reveal();
    };

    const fromLink = (link) => ({
      src: link.dataset.dvkPreview,
      href: link.getAttribute('href'),
      title: link.dataset.dvkPreviewTitle,
      count: link.dataset.dvkPreviewCount,
    });

    const queue = (data) => {
      clearTimeout(timer);
      timer = setTimeout(() => show(data), SWAP_DELAY);
    };

    // Warm the pictures of the column the pointer moves into (a handful of small images),
    // so hovering within it feels instant without loading the whole menu up front.
    const warmed = new Set();
    menu.querySelectorAll('.mega-menu__list > li').forEach((column) => {
      column.addEventListener(
        'pointerenter',
        () => {
          column.querySelectorAll('a[data-dvk-preview]').forEach((link) => {
            const src = link.dataset.dvkPreview;
            if (warmed.has(src)) return;
            warmed.add(src);
            const img = new Image();
            img.fetchPriority = 'low';
            img.decoding = 'async';
            img.src = src;
          });
        },
        { once: true }
      );
    });

    menu.querySelectorAll('a[data-dvk-preview]').forEach((link) => {
      link.addEventListener('pointerenter', () => queue(fromLink(link)));
      link.addEventListener('focus', () => show(fromLink(link)));
    });

    // Back to the menu's own image when the menu closes.
    menu.addEventListener('toggle', () => {
      if (!menu.open) {
        clearTimeout(timer);
        show(start);
      }
    });
  };

  const setup = () => {
    document.querySelectorAll('details.mega-menu').forEach((menu) => {
      // Start fetching the first picture when the pointer heads for the menu, not on click.
      const warm = () => menu.querySelectorAll('.dvk-mega__img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
      const summary = menu.querySelector('summary');
      summary.addEventListener('pointerenter', warm, { once: true });
      summary.addEventListener('focus', warm, { once: true });

      menu.addEventListener('toggle', () => menu.open && init(menu));
      if (menu.open) init(menu);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();

/* "Inpakken als cadeau": the checkbox sets the gift wrap line to 1 or 0 via /cart/update.js,
   then re-renders the same sections the cart itself refreshes after a quantity change. */
if (!customElements.get('dvk-gift-wrap')) {
  customElements.define(
    'dvk-gift-wrap',
    class extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input');
        this.input.addEventListener('change', () => this.set(this.input.checked ? 1 : 0));
        // Only the wrap is left after removing the last product: take it out too.
        if (this.hasAttribute('data-orphan') && this.input.checked) this.set(0);
      }

      host() {
        return this.dataset.context === 'drawer'
          ? document.querySelector('cart-drawer-items')
          : document.querySelector('cart-items');
      }

      async set(quantity) {
        const host = this.host();
        const sections = host && typeof host.getSectionsToRender === 'function' ? host.getSectionsToRender() : [];
        this.setAttribute('aria-busy', 'true');
        this.input.disabled = true;
        try {
          const response = await fetch(`${window.routes.cart_update_url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              updates: { [this.dataset.variantId]: quantity },
              sections: sections.map((s) => s.section),
              sections_url: window.location.pathname,
            }),
          });
          const cart = await response.json();
          if (!response.ok || !cart.sections) throw new Error(cart.description || response.status);

          sections.forEach((s) => {
            const html = cart.sections[s.section];
            const target = document.getElementById(s.id);
            if (!html || !target) return;
            const source = new DOMParser().parseFromString(html, 'text/html').querySelector(s.selector);
            const slot = target.querySelector(s.selector) || target;
            if (source) slot.innerHTML = source.innerHTML;
          });
          // Same empty-state switch as cart.js updateQuantity, for when the wrap was the last line.
          const empty = cart.item_count === 0;
          document.querySelectorAll('cart-drawer, cart-items, cart-drawer-items').forEach((el) => el.classList.toggle('is-empty', empty));
          const footer = document.getElementById('main-cart-footer');
          if (footer) footer.classList.toggle('is-empty', empty);
          if (window.PUB_SUB_EVENTS && typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cart });
          }
          // The drawer re-render replaces this element; put focus back on the new checkbox.
          if (!this.isConnected) {
            const next = document.querySelector(`dvk-gift-wrap[data-context="${this.dataset.context}"] input`);
            if (next) next.focus();
          }
        } catch (e) {
          this.input.checked = !this.input.checked;
          const errors =
            document.getElementById(this.dataset.context === 'drawer' ? 'CartDrawer-CartErrors' : 'cart-errors');
          if (errors) errors.textContent = this.dataset.error;
        } finally {
          if (this.isConnected) {
            this.removeAttribute('aria-busy');
            this.input.disabled = false;
          }
        }
      }
    }
  );
}

/* Product pages: "Terug naar ..." goes back in history when the shopper came from a collection or
   search page here, so they land on the same page with their filters and scroll position. */
(() => {
  const link = document.querySelector('[data-dvk-back]');
  if (!link || !document.referrer) return;
  let from;
  try {
    from = new URL(document.referrer);
  } catch (e) {
    return;
  }
  if (from.origin !== window.location.origin) return;
  if (!/^\/(collections|search)(\/|$)/.test(from.pathname) || /\/products\//.test(from.pathname)) return;

  const target = new URL(link.href, window.location.origin);
  if (from.pathname !== target.pathname) {
    link.querySelector('[data-dvk-back-label]').textContent = link.dataset.dvkBackOverview;
  }
  link.href = from.href;
  link.addEventListener('click', (event) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    if (window.history.length < 2) return;
    event.preventDefault();
    window.history.back();
  });
})();
